<template>
    <pendo-multiselect
        :ref="`${type}Multiselect`"
        class="voc-item-multiselect"
        value-key="id"
        multiple
        searchable
        full-width
        hide-selected-options
        :label-key="labelKey"
        :taggable="isLabelMultiselect"
        :class="`voc-item-multiselect__${location}`"
        :value="localSelected"
        :group-label-key="groupLabelKey"
        :group-options-key="groupOptionsKey"
        :options="filteredOptions"
        :option-height="30"
        :close-on-select="false"
        :placeholder="placeholder"
        :disabled="disabled"
        :loading="loading"
        :internal-search="useInternalSearch"
        :is-valid-new-option="isValidNewOption"
        :popper-options="popperOptions"
        :popup-type="canEditVocItems ? 'grid' : 'listbox'"
        @tag="createNew"
        @input="setSelection"
        @input-value-change="filterOptions"
        @open="onOpen"
        @close="onClose"
        @click-outside="onClickOutside">
        <template #selectedLabel="{ option }">
            <pendo-icon-option :option="option" />
        </template>
        <template #option="{ option, index }">
            <voc-item-multiselect-option
                :option="option"
                :label-key="labelKey"
                :type="type"
                :color="getProductAreaColor(option)"
                :location="location"
                :index="index" />
        </template>
        <template #optionGroup="{ option, index }">
            <voc-item-multiselect-option
                :option="option"
                :label-key="labelKey"
                :type="type"
                :color="getProductAreaColor(option)"
                :location="location"
                :index="index" />
        </template>
        <template #tag="{ option }">
            <voc-item-multiselect-tag
                :option="option"
                :is-open="isOpen"
                :type="type"
                :color="getProductAreaColor(option)"
                @remove-item="removeItem(option, $event)" />
        </template>
        <template #noResult>
            No {{ formattedType }}s found. Consider changing your search.
        </template>
        <template #noData>
            No {{ formattedType }}s found.
        </template>
        <template #beforeList>
            <div
                v-if="validationMessage.length"
                class="voc-item-multiselect__validation">
                {{ validationMessage }}
            </div>
        </template>
        <template
            v-if="showCreateProductArea"
            #footer="{ toggleMenu }">
            <div class="voc-item-multiselect__create-product-area-button">
                <pendo-button
                    theme="app"
                    type="subtle"
                    prefix-icon="plus"
                    label="Create Product Area"
                    @click="$emit('createProductArea', toggleMenu)" />
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoButton, PendoIconOption } from '@pendo/components';
import { EventBus } from '@utils/event-bus.js';
import VocItemMultiselectTag from '@/voc/components/voc-item-multiselect/voc-item-multiselect-tag/voc-item-multiselect-tag.vue';
import VocItemMultiselectOption from '@/voc/components/voc-item-multiselect/voc-item-multiselect-option/voc-item-multiselect-option.vue';
import { mapGetters } from 'vuex';
import {
    findOptionEqualToInput,
    parseLabelInput,
    sortCategoriesAndLabels,
    calculateLabelIndex,
    calculateCategoryIndex,
} from '@/voc/helpers/common.helper';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { NO_CATEGORY_STRING } from '@/voc/constants/constants';
import { GROUP_COLORS } from '@/constants';

export default {
    name: 'VocItemMultiselect',
    components: {
        PendoMultiselect,
        VocItemMultiselectTag,
        VocItemMultiselectOption,
        PendoIconOption,
        PendoButton,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            default: () => [],
        },
        selectedItems: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        location: {
            type: String,
            default: 'table',
        },
        type: {
            type: String,
            required: true,
        },
    },
    emits: ['labelsChanged', 'productAreasChanged', 'appsChanged', 'close', 'createProductArea', 'ownedByUserChanged'],
    data () {
        return {
            isOpen: false,
            localSelected: this.selectedItems,
            useInternalSearch: true,
            filteredOptions: [],
            allOptions: [],
            validationMessage: '',
            popperOptions: {
                class: 'voc-item-multiselect__dropdown',
            },
        };
    },
    computed: {
        ...mapGetters({
            canEditVocItems: 'vocItems/canEditVocItems',
            isAnyLabelModalOpen: 'voc/modals/isAnyLabelModalOpen',
        }),
        placeholder () {
            if (this.disabled) {
                if (!this.selectedItems.length) {
                    return '--';
                }

                return '';
            }

            if (this.location === 'table') return 'Type...';

            return this.isOpen ? 'Type...' : 'Click to add...';
        },
        labelKey () {
            switch (this.type) {
                case 'product-area':
                    return 'name';
                case 'app':
                    return 'displayName';
                case 'ownedByUser':
                    return 'username';
                default:
                    return 'label';
            }
        },
        groupLabelKey () {
            switch (this.type) {
                case 'label':
                    return 'category';
                default:
                    return null;
            }
        },
        groupOptionsKey () {
            switch (this.type) {
                case 'label':
                    return 'labels';
                default:
                    return null;
            }
        },
        isLabelMultiselect () {
            return this.type === 'label';
        },
        formattedType () {
            return this.type.replace('-', ' ');
        },
        showCreateProductArea () {
            return this.type === 'product-area' && this.$can({ action: 'create', entity: 'groups' });
        },
        getProductAreaColor () {
            return (option) => GROUP_COLORS.colorMap[option.color] || null;
        },
    },
    watch: {
        selectedItems (selectedItems) {
            if (!this.isOpen) {
                this.localSelected = selectedItems;
            }
        },
        options: {
            handler (options) {
                this.allOptions = options;
                this.filteredOptions = options;
            },
            immediate: true,
        },
    },
    created () {
        EventBus.$on('multiselectError', this.reset);
        EventBus.$on('labelEdited', this.focusLabelAfterEdit);
        EventBus.$on('categoryEdited', this.updateAndFocusCategoryAfterEdit);
        EventBus.$on('categoryDeleted', this.updateSelectedLabelsAfterCategoryDelete);
    },
    beforeUnmount () {
        EventBus.$off('multiselectError', this.reset);
        EventBus.$off('labelEdited', this.focusLabelAfterEdit);
        EventBus.$off('categoryEdited', this.updateAndFocusCategoryAfterEdit);
        EventBus.$off('categoryDeleted', this.updateSelectedLabelsAfterCategoryDelete);
    },
    methods: {
        setSelection (selectedItems) {
            this.localSelected = selectedItems;
        },
        createNew (str) {
            switch (this.type) {
                case 'label': {
                    const options = cloneDeep(this.allOptions);
                    let { category, label } = parseLabelInput(str);

                    if (!category) {
                        category = NO_CATEGORY_STRING;
                    }

                    const newLabel = { label };
                    const categoryIndex = options.findIndex(
                        (option) => option.category.toLowerCase() === category.toLowerCase(),
                    );

                    if (categoryIndex > -1) {
                        if (category !== NO_CATEGORY_STRING) {
                            newLabel.categoryId = options[categoryIndex].id;
                            newLabel.category = options[categoryIndex].category;
                        } else {
                            newLabel.category = NO_CATEGORY_STRING;
                        }
                        options[categoryIndex].labels.push(newLabel);
                    } else {
                        if (category !== NO_CATEGORY_STRING) {
                            newLabel.category = category;
                        } else {
                            newLabel.category = NO_CATEGORY_STRING;
                        }
                        options.push({
                            id: `new-category-${category.toLowerCase()}`,
                            category,
                            labels: [newLabel],
                        });
                    }

                    this.allOptions = sortCategoriesAndLabels(options);
                    this.localSelected = [...this.localSelected, newLabel];
                    break;
                }
                default:
                    break;
            }
        },
        onOpen () {
            this.isOpen = true;
        },
        onClose () {
            this.isOpen = false;
            if (!isEqual(this.localSelected, this.selectedItems)) {
                switch (this.type) {
                    case 'label':
                        this.$emit('labelsChanged', this.localSelected);
                        break;
                    case 'product-area':
                        this.$emit('productAreasChanged', this.localSelected);
                        break;
                    case 'app':
                        this.$emit('appsChanged', this.localSelected);
                        break;
                    case 'ownedByUser':
                        this.$emit('ownedByUserChanged', this.localSelected);
                        break;
                    default:
                        break;
                }
            }
            this.$emit('close');
        },
        removeItem (itemToRemove) {
            switch (this.type) {
                case 'label':
                    if (itemToRemove.id) {
                        this.localSelected = this.localSelected.filter((item) => {
                            return item.id !== itemToRemove.id;
                        });

                        return;
                    }

                    this.localSelected = this.localSelected.filter((item) => {
                        return item.label !== itemToRemove.label || item.category !== itemToRemove.category;
                    });
                    this.allOptions = this.allOptions.map((option) => {
                        if (option.category === itemToRemove.category) {
                            option.labels = option.labels.filter((label) => {
                                return label.label !== itemToRemove.label;
                            });
                        }

                        return option;
                    });
                    break;
                default:
                    this.localSelected = this.localSelected.filter((item) => {
                        return item.id !== itemToRemove.id;
                    });
                    break;
            }
        },
        reset ({ type }) {
            if (type === this.type) {
                this.localSelected = this.selectedItems;
                this.filteredOptions = this.options;
            }
        },
        isValidNewOption (input) {
            if (!this.canEditVocItems || this.type !== 'label') return false;
            if (input.split(':').length - 1 > 1) {
                this.validationMessage = 'Label cannot contain a colon';

                return false;
            }

            const options = this.allOptions;
            const { category, label } = parseLabelInput(input);
            const bannedCharacters = ['|', ',', '{', '}', '<', '>'];

            if (!label || label.length < 2) {
                this.validationMessage = '';

                return false;
            }
            if (label.length > 255) {
                this.validationMessage = `Too many characters - label ${label.length}/255`;

                return false;
            }
            if (bannedCharacters.some((char) => label.includes(char))) {
                this.validationMessage = 'Label cannot contain special characters |  ,  {  }  <  >';

                return false;
            }

            if (category) {
                if (category.length < 2) {
                    this.validationMessage = 'Category must be at least 2 characters';

                    return false;
                }
                if (category.length > 128) {
                    this.validationMessage = `Too many characters - category ${category.length}/128`;

                    return false;
                }
                if (bannedCharacters.some((char) => category.includes(char))) {
                    this.validationMessage = 'Category cannot contain special characters |  ,  {  }  <  >';

                    return false;
                }
                const existingCategory = findOptionEqualToInput(options, category);

                if (existingCategory && label) {
                    this.validationMessage = '';

                    return !findOptionEqualToInput(existingCategory.labels, label);
                }

                this.validationMessage = '';

                return Boolean(label);
            }

            const noCategory = options.find((category) => category.id === 'no-category');
            if (noCategory) {
                this.validationMessage = '';

                return !findOptionEqualToInput(noCategory.labels, label);
            }

            this.validationMessage = '';

            return true;
        },
        filterOptions (input) {
            if (this.type === 'label') {
                let useInternalSearch = true;
                if (!input.length) this.validationMessage = '';
                const { category, label } = parseLabelInput(input);

                if (category) {
                    let options;
                    const existingCategory = findOptionEqualToInput(this.allOptions, category);

                    if (existingCategory) {
                        useInternalSearch = false;
                        options = cloneDeep(existingCategory);
                        options.labels = options.labels.filter((option) =>
                            option.label.toLowerCase().includes(label.toLowerCase()),
                        );
                        options = [options];
                    } else {
                        options = cloneDeep(this.allOptions);
                    }

                    this.filteredOptions = options;
                } else {
                    this.filteredOptions = this.allOptions;
                }
                this.useInternalSearch = useInternalSearch;
            }
        },
        onClickOutside ($event) {
            if (this.isAnyLabelModalOpen) {
                $event.stop();
            }
        },
        focusLabelAfterEdit (label) {
            if (this.isOpen) {
                this.$refs[`${this.type}Multiselect`].updateInputValue('');
                this.$refs[`${this.type}Multiselect`].focusInput();

                const index = calculateLabelIndex(this.$store, label, this.localSelected);

                this.$refs[`${this.type}Multiselect`].pointerSet(index);
                this.$nextTick(() => {
                    this.$refs[`${this.type}Multiselect`].scrollHighlightedOptionIntoView(index);
                });
            }
        },
        updateAndFocusCategoryAfterEdit (category) {
            if (this.isOpen) {
                this.localSelected = this.localSelected.map((label) => {
                    if (label.categoryId === category.id) {
                        return {
                            ...label,
                            category: category.name,
                        };
                    }

                    return label;
                });

                this.$refs[`${this.type}Multiselect`].updateInputValue('');
                this.$refs[`${this.type}Multiselect`].focusInput();

                const index = calculateCategoryIndex(this.$store, category, this.localSelected);

                this.$refs[`${this.type}Multiselect`].pointerSet(index);
                this.$nextTick(() => {
                    this.$refs[`${this.type}Multiselect`].scrollHighlightedOptionIntoView(index);
                });
            }
        },
        updateSelectedLabelsAfterCategoryDelete (category) {
            if (this.isOpen) {
                this.localSelected = this.localSelected.filter((label) => {
                    return label.categoryId !== category.id;
                });
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';

.voc-item-multiselect {
    :deep(.pendo-multiselect__trigger) {
        transition: none;
        border-color: transparent;
        background: transparent;

        &:hover {
            border-color: var(--palette-gray-100);
            background-color: var(--palette-gray-0);
        }

        &.is-disabled {
            cursor: default;
            border-color: transparent;
        }
    }

    :deep(.pendo-multiselect__value--tags) {
        padding-right: 8px;
    }

    :deep(.pendo-multiselect__trigger--searchable.is-active) {
        border-color: var(--palette-gray-100);
    }

    :deep(.pendo-multiselect__trigger.is-disabled) {
        background-color: var(--palette-gray-20);

        &:hover {
            background-color: var(--palette-gray-20);
        }

        .pendo-multiselect__caret {
            display: none;
        }
    }

    :deep(.pendo-multiselect__input) {
        background: transparent;
    }

    &__validation {
        color: var(--palette-red-70);
        font-weight: 600;
        line-height: 30px;
        padding-left: 16px;
    }

    &__create-product-area-button {
        padding-left: 16px;
        border-top: 1px solid var(--palette-gray-30);
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.voc-item-multiselect {
    &__dropdown {
        z-index: 3000;

        .pendo-multiselect__group .voc-item-multiselect__option {
            padding: 0px;
            font-size: 12px;
        }
    }
}
</style>
